/**
 * The shapes of the runs chart: one column per run, its height the rows Scout
 * sent, split by what happened to them.
 *
 * Columns have a fixed width and pitch, so one run is one narrow column and
 * twenty runs are twenty — the chart grows with the data instead of stretching
 * a single bar across an empty box.
 */

import { outcomeCounts, type Tone } from "@/lib/integrations/words";

export type ChartSegment = { key: string; word: string; tone: Tone; count: number; y: number; height: number };

export type ChartBar = { index: number; x: number; top: number; height: number; segments: ChartSegment[] };

export type ColumnChart = { width: number; height: number; max: number; ticks: number[]; bars: ChartBar[] };

export type ChartOptions = { height: number; pitch: number; barWidth: number; gap: number };

const DEFAULTS: ChartOptions = { height: 96, pitch: 30, barWidth: 18, gap: 2 };

/** A round number at or above `value`, so the axis reads 0 / 7,500 / 15,000. */
export function niceMax(value: number): number {
  if (!(value > 0)) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (step * power >= value) return step * power;
  }
  return 10 * power;
}

export function columnChart(
  runs: { rowsCollected: number; counts: Record<string, number> }[],
  options: Partial<ChartOptions> = {},
): ColumnChart {
  const { height, pitch, barWidth, gap } = { ...DEFAULTS, ...options };
  const max = niceMax(Math.max(0, ...runs.map((run) => run.rowsCollected)));
  const bars = runs.map((run, index): ChartBar => {
    // A run with rows is never thinner than 3px, so it cannot vanish next to a big one.
    const barHeight = run.rowsCollected > 0 ? Math.min(height, Math.max(3, (run.rowsCollected / max) * height)) : 0;
    const parts = outcomeCounts(run.counts);
    const total = parts.reduce((sum, part) => sum + part.count, 0);
    const usable = Math.max(0, barHeight - gap * Math.max(0, parts.length - 1));
    const sizes = parts.map((part) => (total > 0 && usable > 0 ? Math.max(1.5, (part.count / total) * usable) : 0));
    // Small parts are drawn at least 1.5px; take what that adds off the biggest part.
    const over = sizes.reduce((sum, size) => sum + size, 0) - usable;
    if (over > 0 && sizes.length > 0) {
      const biggest = sizes.indexOf(Math.max(...sizes));
      sizes[biggest] = Math.max(1, sizes[biggest] - over);
    }
    let y = height;
    const segments = parts.map((part, i): ChartSegment => {
      y -= sizes[i];
      const segment = { ...part, y, height: sizes[i] };
      y -= gap;
      return segment;
    });
    return { index, x: index * pitch + (pitch - barWidth) / 2, top: height - barHeight, height: barHeight, segments };
  });
  return { width: Math.max(1, runs.length) * pitch, height, max, ticks: [0, max / 2, max], bars };
}

/** Each outcome's rows over a set of runs, for the legend. */
export function outcomeTotals(runs: { counts: Record<string, number> }[]): { key: string; word: string; tone: Tone; count: number }[] {
  const sums: Record<string, number> = {};
  for (const run of runs) {
    for (const part of outcomeCounts(run.counts)) sums[part.key] = (sums[part.key] ?? 0) + part.count;
  }
  return outcomeCounts(sums);
}
